import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dumbbell } from 'lucide-react';
import FrequencyBasedTemplateSelection from './FrequencyBasedTemplateSelection';
import WorkoutTypeSelection from './WorkoutTypeSelection';
import StructuredWorkoutPlan from './StructuredWorkoutPlan';
import ExerciseLogger from './ExerciseLogger';
import { upperLowerTemplate, fullBodyTemplate } from '@/templates';

// Template history utilities
const saveTemplateHistory = (templateType: string) => {
  try {
    const history = JSON.parse(localStorage.getItem('templateHistory') || '{}');
    history.lastUsed = {
      ...history.lastUsed,
      [templateType]: new Date().toISOString()
    };
    localStorage.setItem('templateHistory', JSON.stringify(history));
  } catch (e) {
    console.log('Error saving template history:', e);
  }
};

interface TemplateWorkflowProps {
  onNotification: (message: string, type: 'success' | 'error' | 'pr') => void;
  soundEnabled?: boolean;
}

type WorkflowStep = 'button' | 'frequency' | 'workoutType' | 'structuredPlan' | 'exerciseLogger';
type TemplateType = 'fullBody' | 'upperLower';
type WorkoutType = 'upperA' | 'upperB' | 'lowerA' | 'lowerB' | 'fullBodyA' | 'fullBodyB';

export default function TemplateWorkflow({ onNotification, soundEnabled = false }: TemplateWorkflowProps) {
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('button');
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType | null>(null);
  const [selectedWorkoutType, setSelectedWorkoutType] = useState<WorkoutType | null>(null);
  const [selectedExercises, setSelectedExercises] = useState<Array<{ exercise: string; sets: number; isAlternative?: boolean }>>([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleFrequencySelect = (_frequency: number, templateType: TemplateType) => {
    setSelectedTemplate(templateType);
    setCurrentStep('workoutType');
  };

  const handleWorkoutTypeSelect = (workoutType: WorkoutType) => {
    setSelectedWorkoutType(workoutType);
    setCurrentStep('structuredPlan');
  };

  const handleStartWorkout = (exercises: Array<{ exercise: string; sets: number; isAlternative?: boolean }>) => {
    setSelectedExercises(exercises);
    setCurrentExerciseIndex(0);
    setCurrentStep('exerciseLogger');
  };

  const handleExerciseComplete = () => {
    // Move to next exercise or complete workout
    if (currentExerciseIndex < selectedExercises.length - 1) {
      setCurrentExerciseIndex(prev => prev + 1);
    } else {
      // Workout complete - save history and reset
      if (selectedWorkoutType) {
        saveTemplateHistory(selectedWorkoutType);
      }
      setCurrentStep('button');
      setSelectedTemplate(null);
      setSelectedWorkoutType(null);
      setSelectedExercises([]);
      setCurrentExerciseIndex(0);
    }
  };

  const resetWorkflow = () => {
    setCurrentStep('button');
    setSelectedTemplate(null);
    setSelectedWorkoutType(null);
    setSelectedExercises([]);
    setCurrentExerciseIndex(0);
  };

  const getExercisesForTemplate = () => {
    if (!selectedTemplate || !selectedWorkoutType) return [];
    
    if (selectedTemplate === 'fullBody') {
      return fullBodyTemplate.exercises[selectedWorkoutType as 'fullBodyA' | 'fullBodyB'];
    } else if (selectedTemplate === 'upperLower') {
      return upperLowerTemplate.exercises[selectedWorkoutType as 'upperA' | 'upperB' | 'lowerA' | 'lowerB'];
    }
    
    return [];
  };

  // Render different steps based on current workflow step
  switch (currentStep) {
    case 'frequency':
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="border-border bg-card/50 backdrop-blur-sm">
            <CardContent className="p-6">
              <FrequencyBasedTemplateSelection
                onSelectTemplate={handleFrequencySelect}
                onClose={resetWorkflow}
              />
            </CardContent>
          </Card>
        </motion.div>
      );

    case 'workoutType':
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="border-border bg-card/50 backdrop-blur-sm">
            <CardContent className="p-6">
              <WorkoutTypeSelection
                templateType={selectedTemplate!}
                onSelectWorkoutType={handleWorkoutTypeSelect}
                onBack={() => setCurrentStep('frequency')}
              />
            </CardContent>
          </Card>
        </motion.div>
      );

    case 'structuredPlan':
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="border-border bg-card/50 backdrop-blur-sm">
            <CardContent className="p-6">
              <StructuredWorkoutPlan
                templateType={selectedTemplate!}
                workoutType={selectedWorkoutType!}
                exercises={getExercisesForTemplate()}
                onStartWorkout={handleStartWorkout}
                onBack={() => setCurrentStep('workoutType')}
              />
            </CardContent>
          </Card>
        </motion.div>
      );

    case 'exerciseLogger':
      const currentExercise = selectedExercises[currentExerciseIndex];
      
      return (
        <div className="space-y-6">
          {/* Progress Header */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-green-50/80 to-emerald-50/60 dark:from-green-950/30 dark:to-emerald-950/20 border border-green-200 dark:border-green-800/50 rounded-lg p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Dumbbell className="w-5 h-5 text-green-600 dark:text-green-400" />
                <div>
                  <div className="font-semibold text-green-900 dark:text-green-100">
                    Exercise {currentExerciseIndex + 1} of {selectedExercises.length}: {currentExercise?.exercise}
                  </div>
                  <div className="text-sm text-green-700 dark:text-green-300">
                    {currentExercise?.sets} sets • 
                    {selectedWorkoutType?.charAt(0).toUpperCase()}{selectedWorkoutType?.slice(1)} workout
                  </div>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={resetWorkflow}
                className="h-8 text-xs border-green-200 dark:border-green-800/50"
              >
                Exit Template
              </Button>
            </div>
            
            {/* Manual Navigation Controls */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentExerciseIndex(prev => Math.max(0, prev - 1))}
                disabled={currentExerciseIndex === 0}
                className="h-7 text-xs"
              >
                ← Previous
              </Button>
              
              <div className="text-xs text-green-600 dark:text-green-400">
                Navigate template exercises or log custom exercises freely
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentExerciseIndex(prev => Math.min(selectedExercises.length - 1, prev + 1))}
                disabled={currentExerciseIndex === selectedExercises.length - 1}
                className="h-7 text-xs"
              >
                Next →
              </Button>
            </div>
          </motion.div>

          {/* Exercise Logger */}
          <motion.div
            key={currentExerciseIndex} // Re-mount for each exercise
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ExerciseLogger
              isTransitioning={isTransitioning}
              onNotification={(message, type, exerciseData) => {
                onNotification(message, type);
                
                // Only auto-advance if the logged exercise matches the expected template exercise
                if ((type === 'success' || type === 'pr') && exerciseData) {
                  const loggedExercise = exerciseData.exercise || exerciseData.selectedExercise;
                  if (loggedExercise === currentExercise.exercise) {
                    setIsTransitioning(true);
                    setTimeout(() => {
                      handleExerciseComplete();
                      setIsTransitioning(false);
                    }, 500);
                  }
                  // If different exercise was logged, user can manually advance or stay on current template exercise
                }
              }}
              soundEnabled={soundEnabled}
              templateMode={{
                type: selectedWorkoutType!,
                exercises: [{ // Only show current exercise
                  primary: currentExercise.exercise,
                  alternatives: [],
                  sets: currentExercise.sets,
                  target: ''
                }]
              }}
            />
          </motion.div>
        </div>
      );

    default: // 'button' case
      return (
        <div className="space-y-6">
          {/* Template Selection Button */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Button 
              variant="outline" 
              onClick={() => setCurrentStep('frequency')}
              className="w-full h-12 border-dashed border-primary/50 hover:border-primary hover:bg-primary/5 transition-colors"
            >
              <Dumbbell className="w-4 h-4 mr-2" />
              Start with Workout Template
            </Button>
          </motion.div>

          {/* Regular Exercise Logger */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <ExerciseLogger
              onNotification={onNotification}
              soundEnabled={soundEnabled}
            />
          </motion.div>
        </div>
      );
  }
}